// ---------------------------------------------------------------------------
// datasetUsageQueries — TanStack Query key + option factory for the per-dataset
// usage dashboard on the Dataset Overview tab.
//
// The query key includes the group-by granularity so a future switch from Day
// to Week/Month stays an isolated cache entry. The fetcher closes over the
// injected DataHub IHttpClient (per webapp conventions services stay pure and
// nothing imports a singleton) and returns the raw DatasetUsageResult so the
// cache is the single source of truth; consumers derive view shapes via
// `select`.
// ---------------------------------------------------------------------------

import { queryOptions } from '@tanstack/react-query';

import type { IHttpClient } from '../../../api/client.js';
import { DATASET_USAGE_GROUP_BY_DAY } from '../../../constants/index.js';
import type { DatasetUsageResult } from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';

export const datasetUsageKeys = {
  all: ['datasetUsage'] as const,
  detail: (datasetId: string) =>
    [...datasetUsageKeys.all, datasetId, DATASET_USAGE_GROUP_BY_DAY] as const,
};

/**
 * Aggregated usage for a dataset over the API's default range (last 30 days),
 * bucketed by day. Available to any caller with at least read access (and to
 * public datasets); the API is the authority on access.
 */
export function datasetUsageQuery(client: IHttpClient, datasetId: string) {
  return queryOptions({
    queryKey: datasetUsageKeys.detail(datasetId),
    queryFn: (): Promise<DatasetUsageResult> =>
      new DataHubService(client).getDatasetUsage({
        dataSetId: datasetId,
        groupBy: DATASET_USAGE_GROUP_BY_DAY,
      }),
  });
}
